import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

import {
    REVENUE_DYNAMICS_KPI_SLOTS,
    REVENUE_DYNAMICS_KPI_GRID_CLASS,
} from './revenueDynamicsMeta';

const CHART_HEIGHT_PX = 280;
const PLACEHOLDER_BAR_HEIGHTS = [
    62, 80, 95, 120, 138, 152, 148, 162, 175, 168, 150, 132, 120, 102,
];
const PLACEHOLDER_KPI_COUNT = REVENUE_DYNAMICS_KPI_SLOTS.length;

export const RevenueDynamicsSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.revenueDynamics.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="revenue-dynamics-widget-title"
            {...getNodeTestIdProp('revenue-dynamics-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('revenue-dynamics-widget')}
            >
                <Widget.Header
                    className="pt-4"
                    {...getNodeTestIdProp('revenue-dynamics-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('revenue-dynamics-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'revenue-dynamics-icon-skeleton',
                            )}
                        />
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'revenue-dynamics-widget-subtitle',
                        )}
                    >
                        <Skeleton
                            className="h-3 w-44 rounded-md"
                            {...getNodeTestIdProp(
                                'revenue-dynamics-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content className="flex flex-col gap-4">
                    <Skeleton
                        className="h-8 w-48 rounded-xl"
                        {...getNodeTestIdProp(
                            'revenue-dynamics-product-picker-skeleton',
                        )}
                    />
                    <ul
                        className="flex items-end justify-between gap-2"
                        style={{ height: CHART_HEIGHT_PX }}
                        {...getNodeTestIdProp(
                            'revenue-dynamics-chart-skeleton',
                        )}
                    >
                        {PLACEHOLDER_BAR_HEIGHTS.map((h, index) => (
                            <li
                                key={index}
                                className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
                                {...getNodeTestIdProp(
                                    `revenue-dynamics-bar-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="w-full rounded-t-md"
                                    style={{ height: h }}
                                />
                                <Skeleton className="mt-1 h-2 w-4 rounded-sm" />
                            </li>
                        ))}
                    </ul>
                    <ul
                        className={REVENUE_DYNAMICS_KPI_GRID_CLASS}
                        {...getNodeTestIdProp('revenue-dynamics-kpis-skeleton')}
                    >
                        {Array.from({ length: PLACEHOLDER_KPI_COUNT }).map(
                            (_, index) => (
                                <li
                                    key={index}
                                    className="flex flex-col gap-2 rounded-xl bg-default/40 p-4"
                                    {...getNodeTestIdProp(
                                        `revenue-dynamics-kpi-placeholder-${index}`,
                                    )}
                                >
                                    <Skeleton className="h-2.5 w-20 rounded-md" />
                                    <Skeleton className="h-5 w-24 rounded-md" />
                                </li>
                            ),
                        )}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
